import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import { Menu, X, MapPin, Star, Instagram, Facebook, Youtube, ChevronRight, Sparkles, Scissors, Heart, Phone, Mail, MessageCircle, Crown, CalendarCheck, ShoppingBag, Scan, GraduationCap } from "lucide-react";
import StudentMembershipPage from "./pages/StudentMembershipPage";
import ScalpAnalyzerPage from "./pages/ScalpAnalyzerPage";
import NotableClientsPage from "./pages/NotableClientsPage";
import FloatingBookButton from "./components/FloatingBookButton";
import { CartProvider } from "./context/CartContext";
import ShopPage from "./pages/shop/ShopPage";
import ProductDetailPage from "./pages/shop/ProductDetailPage";
import CartPage from "./pages/shop/CartPage";
import CheckoutPage from "./pages/shop/CheckoutPage";
import OrderConfirmationPage from "./pages/shop/OrderConfirmationPage";
import TrackOrderPage from "./pages/shop/TrackOrderPage";
import AccountAuthPage from "./pages/shop/AccountAuthPage";
import ForgotPasswordPage from "./pages/shop/ForgotPasswordPage";
import ResetPasswordPage from "./pages/shop/ResetPasswordPage";
import AccountDashboardPage from "./pages/shop/AccountDashboardPage";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import { FeaturedProducts } from "./components/shop/FeaturedProducts";
import { shopApi } from "./lib/shopApi";

// Social Media Links
const SOCIAL_LINKS = {
  tiktok: "https://www.tiktok.com/@loctician_carlocs_studio",
  youtube: "https://youtube.com/@loctician_carlocs_studio",
  facebook: "https://www.facebook.com/locticiancarlocsstudio",
  instagram: "https://www.instagram.com/loctician_carlocs_studio",
  pinterest: "https://www.pinterest.com/loctician_carlocs_studio"
};

// Booking Links
const BOOKING_LINKS = {
  personal: "https://www.fresha.com/p/mwezi-mjoli-4489930?share=true&pId=1469337",
  johannesburg: "https://www.fresha.com/book-now/carlocks-studio-c74kx9t4/all-offer?id=1548196&share=true&pId=1469337",
  braamfontein: "https://www.fresha.com/book-now/carlocks-studio-c74kx9t4/all-offer?id=3147095&share=true&pId=1469337"
};

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/fdlrkebv_WhatsApp%20Image%202025-11-21%20at%2012.44.06_ff66975e.jpg";

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Pinterest Icon Component
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.44l1.43-6.05s-.36-.73-.36-1.81c0-1.7.98-2.96 2.21-2.96 1.04 0 1.54.78 1.54 1.72 0 1.05-.67 2.62-1.01 4.07-.29 1.21.61 2.2 1.8 2.2 2.16 0 3.82-2.28 3.82-5.57 0-2.91-2.09-4.95-5.08-4.95-3.46 0-5.49 2.6-5.49 5.28 0 1.05.4 2.17.91 2.78.1.12.11.23.08.35l-.34 1.36c-.05.22-.18.27-.4.16-1.5-.7-2.44-2.89-2.44-4.65 0-3.78 2.75-7.26 7.93-7.26 4.17 0 7.4 2.97 7.4 6.93 0 4.14-2.61 7.46-6.22 7.46-1.22 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.14A12 12 0 1 0 12 0z"/>
  </svg>
);

// Navigation Component
const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [promoVisible, setPromoVisible] = useState(true);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    shopApi.get("/shop/config")
      .then((r) => setAnnouncement(r.data.shop_announcement || ""))
      .catch(() => {});
  }, []);

  const navItems = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Shop", href: "/shop", isPage: true },
    { label: "Locations", href: "#studio" },
    { label: "Student Membership", href: "/student-membership", isPage: true },
    { label: "Scalp Analyzer", href: "/scalp-analyzer", isPage: true },
    { label: "Notable Clients", href: "/notable-clients", isPage: true },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className="fixed w-full z-50 top-0 bg-black border-b border-white/10"
    >
      {/* Announcement Bar — slim gold promo banner (text editable in Admin → Settings) */}
      {promoVisible && announcement && (
        <div data-testid="announcement-bar" className="relative bg-[#D4AF37] text-[#050505]">
          <div className="max-w-7xl mx-auto px-10 py-1.5 flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <Link
              to="/shop"
              data-testid="announcement-link"
              className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.06em] md:tracking-[0.15em] text-center hover:underline underline-offset-2"
            >
              {announcement}
            </Link>
            <button
              onClick={() => setPromoVisible(false)}
              data-testid="announcement-close"
              aria-label="Dismiss announcement"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:opacity-60 transition-opacity"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Loctician Carlocs - Johannesburg loctician logo" 
              className="h-12 md:h-14 w-auto"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              item.isPage ? (
                <Link
                  key={item.label}
                  to={item.href}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className="text-xs uppercase tracking-widest text-white/80 hover:text-[#D4AF37] transition-colors duration-300"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className="text-xs uppercase tracking-widest text-white/80 hover:text-[#D4AF37] transition-colors duration-300"
                >
                  {item.label}
                </a>
              )
            ))}
            <a
              href={BOOKING_LINKS.personal}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-now-btn"
              className="btn-gold text-xs"
            >
              Book Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden mt-4 pb-4 border-t border-white/10">
            <div className="flex flex-col gap-4 pt-4">
              {navItems.map((item) => (
                item.isPage ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-sm uppercase tracking-widest text-white/80 hover:text-[#D4AF37] transition-colors"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-sm uppercase tracking-widest text-white/80 hover:text-[#D4AF37] transition-colors"
                  >
                    {item.label}
                  </a>
                )
              ))}
              <a
                href={BOOKING_LINKS.personal}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold text-center mt-2"
              >
                Book Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="hero" 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/yljd2fn2_Screenshot_20260318_170331_Instagram.jpg"
          alt="Johannesburg locs styling by Loctician Carlocs"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
        <div className="fade-in">
          {/* Social Proof Line */}
          <p className="text-white/60 text-xs md:text-sm mb-4 font-light tracking-wide">
            Trusted by clients across Johannesburg and international visitors.
          </p>
          
          {/* Main Headline */}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-white mb-6 leading-tight font-semibold">
            Loctician Carlocs<br />
            <span className="gold-text italic">- Locs Specialist in Johannesburg</span>
          </h1>

          {/* Star Rating & Reviews — links to Google Business profile */}
          <a
            href="https://share.google/nDKtux25p5axarNEr"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-rating"
            className="group inline-flex items-center justify-center gap-2.5 mb-6 hover:opacity-90 transition-opacity duration-300"
          >
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
              ))}
            </div>
            <p className="text-white/70 text-sm group-hover:text-white transition-colors duration-300">
              <span className="text-[#D4AF37] font-semibold">5.0</span> · 289+ Reviews
            </p>
          </a>

          {/* Intro Paragraph */}
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-8 font-light leading-relaxed">
            Loctician Carlocs is Johannesburg's go-to name for premium loc care — retwists, microlocs, instant locs and dreadlock maintenance, crafted with 12+ years of expertise. Book your appointment today.
          </p>

          {/* Quick Actions — Book, Shop, Analyzer, Membership, Services, Call & WhatsApp */}
          <div className="max-w-3xl mx-auto mb-10" data-testid="hero-actions">
            <a
              href={BOOKING_LINKS.personal}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-now-btn"
              className="btn-gold w-full sm:w-auto sm:min-w-[340px] inline-flex items-center justify-center gap-2.5 mb-4"
            >
              <CalendarCheck className="w-5 h-5" />
              Book Now
            </a>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Link
                to="/shop"
                data-testid="hero-store-btn"
                className="btn-outline-gold flex items-center justify-center gap-1.5 sm:gap-2 !px-2 sm:!px-4 !py-3 sm:!py-3.5 !text-[10px] sm:!text-xs !tracking-[0.08em] text-center"
              >
                <ShoppingBag className="w-4 h-4 shrink-0" />
                Store
              </Link>
              <Link
                to="/scalp-analyzer"
                data-testid="hero-analyzer-btn"
                className="btn-outline-gold flex items-center justify-center gap-1.5 sm:gap-2 !px-2 sm:!px-4 !py-3 sm:!py-3.5 !text-[10px] sm:!text-xs !tracking-[0.08em] text-center"
              >
                <Scan className="w-4 h-4 shrink-0" />
                Scalp Analyzer
              </Link>
              <Link
                to="/student-membership"
                data-testid="hero-membership-btn"
                className="btn-outline-gold flex items-center justify-center gap-1.5 sm:gap-2 !px-2 sm:!px-4 !py-3 sm:!py-3.5 !text-[10px] sm:!text-xs !tracking-[0.08em] text-center"
              >
                <GraduationCap className="w-4 h-4 shrink-0" />
                Student Membership
              </Link>
              <a
                href="#services"
                data-testid="hero-services-btn"
                className="btn-outline-gold flex items-center justify-center gap-1.5 sm:gap-2 !px-2 sm:!px-4 !py-3 sm:!py-3.5 !text-[10px] sm:!text-xs !tracking-[0.08em] text-center"
              >
                <Scissors className="w-4 h-4 shrink-0" />
                Services
              </a>
              <a
                href="tel:+27788583255"
                data-testid="hero-call-btn"
                className="btn-outline-gold flex items-center justify-center gap-1.5 sm:gap-2 !px-2 sm:!px-4 !py-3 sm:!py-3.5 !text-[10px] sm:!text-xs !tracking-[0.08em] text-center"
              >
                <Phone className="w-4 h-4 shrink-0" />
                +27 78 858 3255
              </a>
              <a
                href="https://wa.me/27788583255"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="btn-outline-gold flex items-center justify-center gap-1.5 sm:gap-2 !px-2 sm:!px-4 !py-3 sm:!py-3.5 !text-[10px] sm:!text-xs !tracking-[0.08em] text-center"
              >
                <MessageCircle className="w-4 h-4 shrink-0" />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-[#D4AF37] rounded-full mt-2"></div>
        </div>
      </div>
    </section>
  );
};

// Trusted By Strip — notable clients credibility teaser
const TrustedBySection = () => {
  const clients = [
    { name: "Luyanda Zwane", handle: "luyanda__zwane", image: "/assets/clients/luyanda-zwane.jpg" },
    { name: "Jaykatana", handle: "jaykatana", image: "/assets/clients/jaykatana.jpg" },
    { name: "The Bomb RSA", handle: "thebomb.rsa", image: "/assets/clients/the-bomb-rsa.jpg" },
    { name: "Relebohile", handle: "mpopii.doll", image: "/assets/clients/mpopii-doll.jpg" },
    { name: "King Ginger", handle: "kingginger_sa", image: "/assets/clients/king-ginger.jpg" },
  ];

  return (
    <section data-testid="trusted-by-section" className="py-20 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <p className="text-[#D4AF37] text-xs uppercase tracking-[0.4em] mb-3 flex items-center justify-center gap-2">
          <Crown className="w-4 h-4" /> Trusted By
        </p>
        <h2 className="font-serif text-2xl md:text-3xl text-white/90 mb-12">
          South Africa's Notable Names
        </h2>

        <div className="flex flex-wrap items-start justify-center gap-8 md:gap-14 mb-10">
          {clients.map((client) => (
            <div key={client.name} data-testid={`trusted-by-${client.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group w-28 md:w-32">
              <div className="relative mx-auto w-24 h-24 md:w-28 md:h-28 mb-4">
                <div className="absolute -inset-1.5 rounded-full border border-[#D4AF37]/30 transition-all duration-500 group-hover:border-[#D4AF37] group-hover:scale-105" />
                <img
                  src={client.image}
                  alt={`${client.name} - Johannesburg locs client`}
                  loading="lazy"
                  className="w-full h-full rounded-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <p className="font-serif text-sm md:text-base text-white/90">{client.name}</p>
              {client.handle && <p className="text-white/40 text-xs mt-1">@{client.handle}</p>}
            </div>
          ))}
        </div>

        <Link
          to="/notable-clients"
          data-testid="trusted-by-view-all-link"
          className="group inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-[#D4AF37] transition-colors duration-300"
        >
          Meet The Carlocs Circle
          <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image with Luxury Frame */}
          <div className="relative">
            {/* Gold decorative elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-l-2 border-t-2 border-[#D4AF37] z-10"></div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-[#D4AF37] z-10"></div>
            
            {/* Main image container */}
            <div className="relative aspect-square overflow-hidden bg-[#121212]">
              <img
                src="https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/sobl3yuv_IMG-20260309-WA0008.jpg"
                alt="Johannesburg loctician Carlocs working on client's locs"
                className="w-full h-full object-cover"
              />
              {/* Subtle vignette overlay */}
              <div className="absolute inset-0 shadow-[inset_0_0_100px_rgba(0,0,0,0.3)]"></div>
            </div>
            
            {/* Experience badge */}
            <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-[#050505] border border-[#D4AF37] p-4 md:p-6 z-20">
              <p className="font-serif text-3xl md:text-4xl gold-text">12+</p>
              <p className="text-white/50 text-xs uppercase tracking-widest mt-1">Years of Mastery</p>
            </div>
          </div>

          {/* Content */}
          <div>
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">About The Artist</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-8">
              The Journey of<br />
              <span className="italic gold-text">Loctician Carlocs</span>
            </h2>
            <div className="space-y-6 text-white/70 font-light leading-relaxed">
              <p>
                With over 12 years of dedicated experience in loc care and styling, Loctician Carlocs has 
                established himself as a respected authority in the locs industry. His journey began with 
                a passion for hair artistry and has evolved into a premium personal brand synonymous with 
                excellence and professionalism.
              </p>
              <p>
                Known for his meticulous attention to detail and innovative styling techniques, Carlocs 
                has built a reputation that extends far beyond local boundaries. Clients from different 
                cities and countries seek his expertise, trusting him with the care of their crowns.
              </p>
              <p>
                Today, the Loctician Carlocs brand encompasses multiple ventures: the acclaimed Loctician 
                Carlocs salon with two locations in Johannesburg, the lifestyle brand Carlocs 
                Culture, and the upcoming Loctician Carlocs Academy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    { name: "Retwists", description: "Regular maintenance to keep your locs neat and healthy" },
    { name: "Microlocs", description: "Smaller, more refined locs for a unique look" },
    { name: "Instant Locs", description: "Get your loc journey started immediately" },
    { name: "Loc Repairs", description: "Fix thinning, breakage, or damaged locs" },
    { name: "Detox Treatments", description: "Deep cleanse to remove buildup and restore freshness" },
    { name: "Deep Conditioning", description: "Intensive moisture and nourishment for your locs" },
    { name: "Starter Locs", description: "Begin your loc journey with expert guidance" },
    { name: "Loc Reconstruction", description: "Rebuild and restore damaged or neglected locs" },
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">What We Offer</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-6">
            Professional <span className="italic gold-text">Loc Services</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto font-light">
            Each service is designed to maintain, restore, and elevate your locs while ensuring 
            long-term health and a clean, polished look.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {services.map((service, index) => (
            <div 
              key={index}
              className="lux-card p-6 text-center hover:bg-[#181818] transition-colors duration-300"
            >
              <h3 className="font-serif text-lg text-white mb-2">{service.name}</h3>
              <p className="text-white/50 text-sm font-light">{service.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <a
            href={BOOKING_LINKS.personal}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-book-btn"
            className="btn-gold"
          >
            Book Appointment
          </a>
        </div>
      </div>
    </section>
  );
};

// House Call Booking Section
const HouseCallSection = () => {
  return (
    <section id="housecall" data-testid="housecall-section" className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 border-l border-t border-[#D4AF37]/10"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 border-r border-b border-[#D4AF37]/10"></div>
      
      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        {/* Icon */}
        <div className="w-20 h-20 border border-[#D4AF37] flex items-center justify-center mx-auto mb-8">
          <svg className="w-10 h-10 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>

        {/* Content */}
        <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Mobile Services</p>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-6">
          Book a <span className="italic gold-text">House Call</span>
        </h2>
        <p className="text-white/60 text-lg md:text-xl font-light max-w-2xl mx-auto mb-10">
          Professional loc services delivered to your location for comfort, privacy, and convenience.
        </p>

        {/* CTA Button */}
        <a
          href="https://www.fresha.com/book-now/carlocks-studio-c74kx9t4/all-offer?id=2929235&share=true&pId=1469337"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="housecall-book-btn"
          className="inline-block bg-[#D4AF37] hover:bg-[#B5952F] text-[#050505] font-semibold px-12 py-5 uppercase tracking-[0.2em] text-sm transition-all duration-300 hover:-translate-y-1"
        >
          Book House Call
        </a>

        {/* Note */}
        <p className="text-white/40 text-sm mt-8 font-light">
          Travel applies based on location. Advance booking is recommended.
        </p>

        {/* Decorative Line */}
        <div className="mt-12 flex items-center justify-center gap-4">
          <div className="w-16 h-px bg-[#D4AF37]/30"></div>
          <div className="w-2 h-2 border border-[#D4AF37]/50 rotate-45"></div>
          <div className="w-16 h-px bg-[#D4AF37]/30"></div>
        </div>
      </div>
    </section>
  );
};

// Locations Section
const StudioSection = () => {
  const services = [
    "Retwists", "Loc Styling", "Instant Locs", "Microlocs",
    "Loc Repairs", "Detox Treatments", "Deep Conditioning",
    "Starter Locs", "Loc Reconstruction", "Maintenance"
  ];

  return (
    <section id="studio" data-testid="studio-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Our Locations</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
            Loctician Carlocs <span className="italic gold-text">Locations</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-2xl mx-auto">
            Our physical locs salons where expert loc services are performed. 
            Now with two convenient locations in Johannesburg.
          </p>
        </div>

        {/* Location Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Johannesburg CBD */}
          <div className="lux-card overflow-hidden group">
            <div className="aspect-video overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/vzz93mxn_IMG-20260318-WA0043.jpg"
                alt="Johannesburg loctician salon interior - Loctician Carlocs"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-white/50 text-sm uppercase tracking-wider">Main Location</span>
              </div>
              <h3 className="font-serif text-2xl text-white mb-3">Loctician Carlocs — Johannesburg CBD</h3>
              <div className="text-white/50 text-sm mb-6 space-y-1">
                <p>Shop No 7 Upstairs</p>
                <p>Peter House / Mobile Centre Building</p>
                <p>233 Lilian Ngoyi Street</p>
                <p>Johannesburg, 2000</p>
              </div>
              <a
                href={BOOKING_LINKS.johannesburg}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="studio-jhb-book-btn"
                className="btn-gold w-full text-center block"
              >
                Book Now
              </a>
            </div>
          </div>

          {/* Braamfontein */}
          <div className="lux-card overflow-hidden group">
            <div className="aspect-video overflow-hidden bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-12 h-12 text-[#D4AF37] mx-auto mb-3" />
                <p className="font-serif text-xl text-white">Braamfontein</p>
                <p className="text-white/40 text-xs uppercase tracking-widest mt-1">New Location</p>
              </div>
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-white/50 text-sm uppercase tracking-wider">New Location</span>
              </div>
              <h3 className="font-serif text-2xl text-white mb-3">Loctician Carlocs — Braamfontein</h3>
              <div className="text-white/50 text-sm mb-4 space-y-1">
                <p>93 Juta Street</p>
                <p>Osiphile Botle Building</p>
                <p>Braamfontein, Johannesburg, 2017</p>
              </div>
              <p className="text-[#D4AF37]/80 text-xs mb-6 italic">
                Entrance is inside the passage, not street-facing — several salons share the building.
              </p>
              <a
                href={BOOKING_LINKS.braamfontein}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="studio-braamfontein-book-btn"
                className="btn-gold w-full text-center block"
              >
                Book Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Portfolio Gallery Section
const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  
  const galleryImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/wwyse4y2_Screenshot_20260318_170752_Instagram.jpg",
      alt: "Johannesburg locs - elegant red dress glam look",
      style: "Glam Locs"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/pigj0doi_Screenshot_20260318_164623_Instagram.jpg",
      alt: "Johannesburg locs styling - elegant updo with hanging locs",
      style: "Loc Styling"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/cd3ppkeh_Screenshot_20260318_170347_Instagram.jpg",
      alt: "Johannesburg locs - barrel curl locs portrait",
      style: "Barrel Curls"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/lctsj7p0_Screenshot_20260318_170527_Instagram.jpg",
      alt: "Johannesburg locs - high ponytail lifestyle shot",
      style: "High Ponytail"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/64dc6zi1_Screenshot_20260318_164310_Instagram.jpg",
      alt: "Johannesburg locs - two-tone ombre effect",
      style: "Ombre Locs"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/50z5czmb_Screenshot_20260318_170331_Instagram.jpg",
      alt: "Johannesburg locs - natural barrel curl portrait",
      style: "Natural Curls"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/lqoun3f3_Screenshot_20260318_164449_Instagram.jpg",
      alt: "Johannesburg locs - elegant loc bun updo",
      style: "Loc Updo"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/hlu2seob_Screenshot_20260318_170649_Instagram.jpg",
      alt: "Johannesburg locs - curly locs with face framing",
      style: "Face Framing"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/9xa584fx_IMG-20260312-WA0009.jpg",
      alt: "Johannesburg locs - curly loc ends styling",
      style: "Curly Ends"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/64gupopt_IMG-20260310-WA0019.jpg",
      alt: "Johannesburg locs - voluminous loc flower bun",
      style: "Loc Bun"
    }
  ];

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Our Work</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
            Transformation <span className="italic gold-text">Gallery</span>
          </h2>
          <p className="text-white/50 mt-4">Real results from our chair</p>
        </div>

        {/* Clean grid layout */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {galleryImages.map((image, index) => (
            <div 
              key={index}
              data-testid={`gallery-image-${index}`}
              className="group relative aspect-square overflow-hidden cursor-pointer lux-card"
              onClick={() => setSelectedImage(image)}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                <div className="p-3 md:p-4">
                  <p className="text-white text-xs md:text-sm font-medium">{image.style}</p>
                </div>
              </div>
            </div>
          ))}
          {/* View More on Instagram */}
          <div className="aspect-square bg-[#121212] border border-[#27272A] flex items-center justify-center hover:border-[#D4AF37]/50 transition-colors duration-300">
            <a 
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-view-more"
              className="text-center p-4"
            >
              <Instagram className="w-6 h-6 md:w-8 md:h-8 text-[#D4AF37] mx-auto mb-2 md:mb-3" />
              <p className="text-white/60 text-xs uppercase tracking-wider">View More</p>
              <p className="text-white/40 text-xs mt-1">on Instagram</p>
            </a>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            data-testid="gallery-close-modal"
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <div className="max-w-4xl max-h-[90vh] flex flex-col items-center">
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[85vh] object-contain rounded-sm"
            />
            <p className="text-center text-white/60 mt-4 text-sm">{selectedImage.style}</p>
          </div>
        </div>
      )}
    </section>
  );
};

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Contact & Locations Section
const ContactLocationsSection = () => {
  return (
    <section id="contact" data-testid="contact-locations-section" className="py-24 md:py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Get in Touch</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
            Contact & <span className="italic gold-text">Locations</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-2xl mx-auto">
            Reach out directly or visit one of our locations
          </p>
        </div>

        {/* Contact Details */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="lux-card p-8 md:p-12">
            <div className="grid md:grid-cols-3 gap-8 mb-10">
              {/* Phone */}
              <div className="text-center">
                <div className="w-14 h-14 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Phone</p>
                <a 
                  href="tel:+27788583255" 
                  data-testid="contact-phone-link"
                  className="text-white text-lg hover:text-[#D4AF37] transition-colors"
                >
                  +27 78 858 3255
                </a>
              </div>

              {/* Email */}
              <div className="text-center">
                <div className="w-14 h-14 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Email</p>
                <a 
                  href="mailto:locticiancarlocs@gmail.com" 
                  data-testid="contact-email-link"
                  className="text-white text-lg hover:text-[#D4AF37] transition-colors break-all"
                >
                  locticiancarlocs@gmail.com
                </a>
              </div>

              {/* WhatsApp */}
              <div className="text-center">
                <div className="w-14 h-14 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">WhatsApp</p>
                <p className="text-white text-lg">Quick Assistance</p>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <div className="text-center border-t border-[#27272A] pt-8">
              <a
                href="https://wa.me/message/Z46J6MKBY4C6K1"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold px-8 py-4 rounded-sm transition-colors duration-300 uppercase tracking-widest text-sm"
              >
                <WhatsAppIcon className="w-5 h-5" />
                Chat on WhatsApp
              </a>
              <p className="text-white/40 text-sm mt-4 max-w-md mx-auto">
                Message Loctician Carlocs directly on WhatsApp for quick assistance.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

// Social Media Section
const SocialSection = () => {
  const socials = [
    { name: "TikTok", icon: TikTokIcon, href: SOCIAL_LINKS.tiktok },
    { name: "YouTube", icon: Youtube, href: SOCIAL_LINKS.youtube },
    { name: "Facebook", icon: Facebook, href: SOCIAL_LINKS.facebook },
    { name: "Instagram", icon: Instagram, href: SOCIAL_LINKS.instagram },
    { name: "Pinterest", icon: PinterestIcon, href: SOCIAL_LINKS.pinterest },
  ];

  return (
    <section data-testid="social-section" className="py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Stay Connected</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12">
          Follow the <span className="italic gold-text">Journey</span>
        </h2>

        <div className="flex flex-wrap justify-center gap-6">
          {socials.map((social) => (
            <a
              key={social.name}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-${social.name.toLowerCase()}-link`}
              className="group flex flex-col items-center gap-3"
            >
              <div className="w-16 h-16 border border-[#27272A] flex items-center justify-center group-hover:border-[#D4AF37] transition-colors duration-300">
                <social.icon className="w-6 h-6 text-white/60 group-hover:text-[#D4AF37] transition-colors duration-300" />
              </div>
              <span className="text-white/40 text-xs uppercase tracking-wider group-hover:text-white transition-colors duration-300">
                {social.name}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <img 
            src={LOGO_URL} 
            alt="Loctician Carlocs - Johannesburg loctician logo" 
            className="h-24 md:h-32 w-auto mx-auto mb-6"
          />
          <p className="text-white/30 text-sm uppercase tracking-widest">
            The Art of Crown Care
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-8 mb-16">
          <div>
            <p className="text-white text-sm uppercase tracking-wider mb-4">Brand</p>
            <ul className="space-y-2 text-white/50 text-sm">
              <li><a href="#about" className="hover:text-[#D4AF37] transition-colors">About</a></li>
              <li><a href="#studio" className="hover:text-[#D4AF37] transition-colors">Locations</a></li>
              <li><a href="#gallery" className="hover:text-[#D4AF37] transition-colors">Gallery</a></li>
            </ul>
          </div>
          <div>
            <p className="text-white text-sm uppercase tracking-wider mb-4">Book</p>
            <ul className="space-y-2 text-white/50 text-sm">
              <li><a href={BOOKING_LINKS.personal} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">Personal Booking</a></li>
              <li><a href={BOOKING_LINKS.johannesburg} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">Johannesburg CBD</a></li>
              <li><a href={BOOKING_LINKS.braamfontein} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">Braamfontein</a></li>
            </ul>
          </div>
          <div>
            <p className="text-white text-sm uppercase tracking-wider mb-4">Ventures</p>
            <ul className="space-y-2 text-white/50 text-sm">
              <li><Link to="/shop" className="hover:text-[#D4AF37] transition-colors">Shop</Link></li>
              <li><Link to="/notable-clients" className="hover:text-[#D4AF37] transition-colors">Notable Clients</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-white text-sm uppercase tracking-wider mb-4">Locations</p>
            <ul className="space-y-2 text-white/50 text-sm">
              <li>Johannesburg CBD</li>
              <li>Braamfontein</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-sm">
            © {currentYear} Loctician Carlocs. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {[
              { icon: TikTokIcon, href: SOCIAL_LINKS.tiktok },
              { icon: Youtube, href: SOCIAL_LINKS.youtube },
              { icon: Instagram, href: SOCIAL_LINKS.instagram },
              { icon: Facebook, href: SOCIAL_LINKS.facebook },
            ].map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#D4AF37] transition-colors duration-300"
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navigation />
      <HeroSection />
      <TrustedBySection />
      <AboutSection />
      <ServicesSection />
      <StudioSection />
      <HouseCallSection />
      <FeaturedProducts />
      <GallerySection />
      <ContactLocationsSection />
      <SocialSection />
      <Footer />
      <FloatingBookButton />
    </div>
  );
};

// Main App Component with Router
function App() {
  return (
    <CartProvider>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/student-membership" element={<StudentMembershipPage />} />
          <Route path="/scalp-analyzer" element={<ScalpAnalyzerPage />} />
          <Route path="/notable-clients" element={<NotableClientsPage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/product/:slug" element={<ProductDetailPage />} />
          <Route path="/shop/order/:orderNumber" element={<OrderConfirmationPage />} />
          <Route path="/track" element={<TrackOrderPage />} />
          <Route path="/account" element={<AccountDashboardPage />} />
          <Route path="/account/login" element={<AccountAuthPage mode="login" />} />
          <Route path="/account/register" element={<AccountAuthPage mode="register" />} />
          <Route path="/account/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/account/reset-password" element={<ResetPasswordPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </Router>
    </CartProvider>
  );
}

export default App;
